/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('scrolling-image-list')) {
  class ScrollingImageList extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.images = this.querySelectorAll('.scrolling-image-list__image');

      // Interactive elements are only required if there is more than one image
      if (this.images.length > 1) {
        this.contentColumn = this.querySelector('.scrolling-image-list__content-column');
        this.featureText = this.querySelector('.scrolling-image-list__content-column .feature-text-paired');

        const mq = window.matchMedia('(min-width: 768px)');
        this.addListeners(mq);
        mq.addEventListener('change', (event) => {
          this.removeListeners();
          this.addListeners(event);
        });
      }
      this.setAttribute('loaded', '');
    }

    /**
     * Add event listeners for this element.
     * @param {MediaQueryList|MediaQueryListEvent} mq - MediaQuery object determining flip between
     * carousel & scroll modes.
     */
    addListeners(mq) {
      if (mq.matches) {
        this.throttledHandleScroll = throttle(this.handleScroll.bind(this), 100);
        window.addEventListener('scroll', this.throttledHandleScroll);
        this.boundUpdatePositioning = this.updatePositioning.bind(this);
        window.addEventListener('scroll', this.boundUpdatePositioning);
        window.addEventListener('resize', this.boundUpdatePositioning);

        // After binding scroll events, run them
        this.throttledHandleScroll();
        this.boundUpdatePositioning();

        this.contentClickListeners = [];
        this.querySelectorAll('.scrolling-image-list__image').forEach((el) => {
          const oc = () => this.scrollToImage(el.dataset.index);
          this.contentClickListeners.push({ el, oc });
          el.addEventListener('click', oc);
        });

        // Keyboard tabbing
        this.delegatedKeydownHandler = theme.addDelegateEventListener(this, 'keydown', '.scrolling-image-list__image, .scrolling-image-list__content:not(.scrolling-image-list__content--mobile), .scrolling-image-list__content:not(.scrolling-image-list__content--mobile) a', this.handleKeydown.bind(this));
      } else {
        this.addEventListener('on:carousel-slider:select', this.handleSliderSelect.bind(this));

        // --- Adaptive mobile slider height: ONLY for the Key Features section ---
        // Every other section using <scrolling-image-list> is untouched.
        if (this.closest('.section-key-features')) {
          this.sliderEl = this.querySelector('.slider');
          this.itemEls = this.querySelectorAll('.slider__item');

          if (this.sliderEl && this.itemEls.length) {
            this.lastWindowWidth = window.innerWidth;
            this.lastSwipeTime = 0;

            // Only trust 'select' events that follow an actual HORIZONTAL
            // swipe/drag on the slider — not just any touch. A vertical
            // page-scroll gesture also starts with a touch landing on the
            // slider (it fills the section), so touchstart alone can't tell
            // "swiping the carousel" apart from "scrolling past it". We wait
            // for the touch to move and compare horizontal vs vertical
            // distance before deciding. This is what stops vertical page
            // scrolling from re-triggering (and reverting) the adaptive
            // height — only a genuine horizontal swipe updates it.
            this.touchStartX = 0;
            this.touchStartY = 0;
            this.touchDirectionDetected = false;

            this.boundTouchStart = (evt) => {
              const touch = evt.touches ? evt.touches[0] : evt;
              this.touchStartX = touch.clientX;
              this.touchStartY = touch.clientY;
              this.touchDirectionDetected = false;
            };

            this.boundTouchMove = (evt) => {
              if (this.touchDirectionDetected) return;
              const touch = evt.touches ? evt.touches[0] : evt;
              const dx = Math.abs(touch.clientX - this.touchStartX);
              const dy = Math.abs(touch.clientY - this.touchStartY);

              // Wait until movement is unambiguous, then decide once.
              if (dx > 10 || dy > 10) {
                this.touchDirectionDetected = true;
                // Horizontal movement dominates -> real carousel swipe.
                // Vertical movement dominates -> page scroll, ignore it.
                if (dx > dy) {
                  this.lastSwipeTime = Date.now();
                }
              }
            };

            // Mouse/pen drags (not touch) can still be trusted immediately,
            // since there's no vertical-scroll ambiguity for those inputs.
            this.boundMarkSwipe = (evt) => {
              if (evt.pointerType && evt.pointerType !== 'touch') {
                this.lastSwipeTime = Date.now();
              }
            };

            this.sliderEl.addEventListener('pointerdown', this.boundMarkSwipe);
            this.sliderEl.addEventListener('touchstart', this.boundTouchStart, { passive: true });
            this.sliderEl.addEventListener('touchmove', this.boundTouchMove, { passive: true });

            this.boundUpdateSliderHeight = this.updateSliderHeight.bind(this);
            this.throttledUpdateSliderHeight = throttle(() => {
              // Mobile browsers fire 'resize' on scroll (address bar show/hide),
              // which only changes viewport HEIGHT, not width. Ignore those so
              // height doesn't jump on scroll — only recalc on real width
              // changes (rotation, window resize).
              if (window.innerWidth === this.lastWindowWidth) return;
              this.lastWindowWidth = window.innerWidth;
              this.boundUpdateSliderHeight();
            }, 100);
            window.addEventListener('resize', this.throttledUpdateSliderHeight);

            // Set initial height once the active item's own images/videos
            // have loaded, so it's correct from the start.
            this.updateSliderHeight();
            const activeMedia = this.itemEls[0].querySelectorAll('img, video');
            activeMedia.forEach((mediaEl) => {
              const evt = mediaEl.tagName === 'VIDEO' ? 'loadedmetadata' : 'load';
              if (mediaEl.tagName === 'IMG' && mediaEl.complete) return;
              mediaEl.addEventListener(evt, this.boundUpdateSliderHeight, { once: true });
            });
          }
        }
      }
    }

    removeListeners() {
      window.removeEventListener('scroll', this.throttledHandleScroll);
      window.removeEventListener('scroll', this.boundUpdatePositioning);
      window.removeEventListener('resize', this.boundUpdatePositioning);
      if (this.contentClickListeners) {
        for (let i = 0; i < this.contentClickListeners.length; i += 1) {
          this.contentClickListeners[i].el.removeEventListener('click', this.contentClickListeners[i].oc);
        }
      }
      this.removeEventListener('click', this.delegatedKeydownHandler);

      // --- Clean up adaptive mobile slider height listeners (Key Features only) ---
      if (this.throttledUpdateSliderHeight) {
        window.removeEventListener('resize', this.throttledUpdateSliderHeight);
        this.throttledUpdateSliderHeight = null;
      }
      if (this.sliderEl && this.boundMarkSwipe) {
        this.sliderEl.removeEventListener('pointerdown', this.boundMarkSwipe);
        this.sliderEl.removeEventListener('touchstart', this.boundTouchStart);
        this.sliderEl.removeEventListener('touchmove', this.boundTouchMove);
      }
      if (this.sliderEl) {
        this.sliderEl.style.removeProperty('height');
        this.sliderEl = null;
      }
    }

    /**
     * Handles throttled 'scroll' events on window.
     */
    handleScroll() {
      let closestIndex = 1;
      let closestDistance = Number.MAX_VALUE;

      this.images.forEach((el, index) => {
        const distance = ScrollingImageList.distanceFromViewportMiddle(el);
        if (distance < closestDistance) {
          closestIndex = index + 1;
          closestDistance = distance;
        }
      });

      this.dataset.currentIndex = closestIndex;
    }

    /**
     * Handles keydown on a selection of elements to aid with tabbing.
     * @param {Event} evt - Keydown event to handle.
     * @param {Element} el - Element this event was triggered on.
     */
    handleKeydown(evt, el) {
      if (evt.code !== 'Tab') return;

      if (el.classList.contains('scrolling-image-list__image')) {
        if (!evt.shiftKey) {
          evt.preventDefault();
          this.querySelector(`.scrolling-image-list__content:not(.scrolling-image-list__content--mobile)[data-index="${el.dataset.index}"]`).focus();
          return;
        }

        if (el.dataset.index !== '1') {
          evt.preventDefault();
          this.querySelector(`.scrolling-image-list__image[data-index="${parseInt(el.dataset.index, 10) - 1}"]`).focus();
          return;
        }

        return;
      }

      if (el.classList.contains('scrolling-image-list__content')) {
        if (evt.shiftKey) {
          evt.preventDefault();
          this.querySelector(`.scrolling-image-list__image[data-index="${el.dataset.index}"]`).focus();
          return;
        }

        if (el.nextElementSibling !== null) {
          evt.preventDefault();
          if (el.querySelector('a')) {
            el.querySelector('a').focus();
          } else {
            this.querySelector(`.scrolling-image-list__image[data-index="${parseInt(el.dataset.index, 10) + 1}"]`).focus();
          }
          return;
        }

        return;
      }

      if (el.tagName === 'A') {
        const contentContainer = el.closest('.scrolling-image-list__content');
        if (contentContainer.nextElementSibling !== null) {
          const lastA = [...contentContainer.querySelectorAll('a')].pop();

          if (lastA === el && !evt.shiftKey) {
            evt.preventDefault();
            this.querySelector(`.scrolling-image-list__image[data-index="${parseInt(contentContainer.dataset.index, 10) + 1}"]`).focus();
          }
        }
      }
    }

    /**
     * Set variables controlling the position of text content.
     */
    updatePositioning() {
      const containerBcr = this.contentColumn.getBoundingClientRect();
      const containerOffset = containerBcr.top + window.scrollY;
      const contentHeight = this.featureText.getBoundingClientRect().height;
      let bcr = this.images[0].getBoundingClientRect();
      const topImageMid = bcr.top + window.scrollY + bcr.height / 2;
      bcr = this.images[this.images.length - 1].getBoundingClientRect();
      const botImageMid = bcr.top + window.scrollY + bcr.height / 2;
      const windowMid = window.scrollY + window.innerHeight / 2;
      if (windowMid < topImageMid) {
        this.style.setProperty('--scrolling-image-list-content-offset-y', `${topImageMid - containerOffset - contentHeight / 2}px`);
        this.style.removeProperty('--scrolling-image-list-content-position');
        this.style.removeProperty('--scrolling-image-list-content-offset-x');
        this.style.removeProperty('--scrolling-image-list-content-width');
      } else if (windowMid > botImageMid) {
        this.style.setProperty('--scrolling-image-list-content-offset-y', `${botImageMid - containerOffset - contentHeight / 2}px`);
        this.style.removeProperty('--scrolling-image-list-content-position');
        this.style.removeProperty('--scrolling-image-list-content-offset-x');
        this.style.removeProperty('--scrolling-image-list-content-width');
      } else {
        this.style.setProperty('--scrolling-image-list-content-position', 'fixed');
        this.style.setProperty('--scrolling-image-list-content-offset-y', `${window.innerHeight / 2 - contentHeight / 2}px`);
        this.style.setProperty('--scrolling-image-list-content-offset-x', `${containerBcr.left}px`);
        this.style.setProperty('--scrolling-image-list-content-width', `${containerBcr.width}px`);
      }
    }

    /**
     * Handles mobile carousel select event.
     * @param {object} evt - Slider event.
     */
    handleSliderSelect(evt) {
      this.dataset.currentIndex = evt.detail.index + 1;

      // Only recalc height for Key Features when the select event followed
      // an actual swipe/drag on the slider — not a page-scroll-triggered one.
      if (this.sliderEl) {
        if (Date.now() - this.lastSwipeTime < 1000) {
          this.updateSliderHeight();
        }
      }
    }

    /**
     * Sync the mobile slider container's height to the currently active card.
     * Only ever called for Key Features instances (see addListeners guard),
     * and guards itself so it's a safe no-op everywhere else.
     */
    updateSliderHeight() {
      if (!this.sliderEl || !this.itemEls || !this.itemEls.length) return;
      const currentIndex = parseInt(this.dataset.currentIndex, 10) || 1;
      const activeItem = this.itemEls[currentIndex - 1];
      if (!activeItem) return;
      this.sliderEl.style.height = `${activeItem.getBoundingClientRect().height}px`;
    }

    /**
     * Scroll to place a specific image in the middle of the viewport.
     * @param {number} index - Index of image to scroll to
     */
    scrollToImage(index) {
      const image = this.images[index - 1];
      window.scrollTo({
        left: 0,
        top: theme.getOffsetTopFromDoc(image) - (window.innerHeight - image.clientHeight) / 2,
        behavior: 'smooth'
      });
    }

    /**
     * Determine distance between middle of element and middle of viewport.
     * @param {Element} el - Element to check
     * @returns {number}
     */
    static distanceFromViewportMiddle(el) {
      const viewportMidY = window.scrollY + window.innerHeight / 2;
      const imageMidY = theme.getOffsetTopFromDoc(el) + el.clientHeight / 2;
      return Math.abs(viewportMidY - imageMidY);
    }
  }

  customElements.define('scrolling-image-list', ScrollingImageList);
}